import React from 'react';
import GuideCallout from './GuideCallout';

const LABELS = { note: 'note', tip: 'tip', warning: 'warning', important: 'important' };

// Concatenate the blockquote's full text and match a leading "Label:",
// e.g. "Note: ..." -> "note".
function detectVariant(children) {
  let text = '';
  const walk = (node) => {
    if (typeof node === 'string') text += node;
    else if (Array.isArray(node)) node.forEach(walk);
    else if (node?.props?.children) walk(node.props.children);
  };
  walk(children);
  const match = text.trim().match(/^([A-Za-z]+):/);
  if (!match) return null;
  return LABELS[match[1].toLowerCase()] || null;
}

// True when `node` is the leading bold "Label:" element (e.g. <strong>Note:</strong>).
function isLeadingLabelStrong(node) {
  if (!React.isValidElement(node) || node.type !== 'strong') return false;
  const inner = React.Children.toArray(node.props.children).join('');
  return /^[A-Za-z]+:$/.test(inner.trim());
}

// react-markdown renders `> **Note:** text` as a <p> whose inline children are
// [<strong>Note:</strong>, " text", ...]. Since GuideCallout draws the styled
// label header itself, drop that redundant leading <strong> (and a single space
// text node after it) from the body. Defensive: any unexpected shape is returned
// unchanged, never throwing.
function stripLeadingLabel(children) {
  const top = React.Children.toArray(children);
  if (top.length === 0) return children;

  const first = top[0];
  if (!React.isValidElement(first) || first.type !== 'p') return children;

  const inline = React.Children.toArray(first.props.children);
  if (inline.length === 0 || !isLeadingLabelStrong(inline[0])) return children;

  let rest = inline.slice(1);
  // Trim a single leading whitespace-only / space-prefixed text node.
  if (typeof rest[0] === 'string') {
    const trimmed = rest[0].replace(/^\s+/, '');
    rest = trimmed === '' ? rest.slice(1) : [trimmed, ...rest.slice(1)];
  }

  const newP = React.cloneElement(first, { children: rest });
  return [newP, ...top.slice(1)];
}

export function buildMarkdownComponents({ onOpenSlug }) {
  return {
    blockquote: ({ children }) => {
      const variant = detectVariant(children);
      if (variant) {
        return <GuideCallout variant={variant}>{stripLeadingLabel(children)}</GuideCallout>;
      }
      return <blockquote>{children}</blockquote>;
    },
    a: ({ href, children }) => {
      if (href?.endsWith('.md')) {
        const slug = href.replace(/\.md$/, '');
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onOpenSlug(slug);
            }}
            style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {children}
          </a>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  };
}
